// The theme environment as WS_WaterGeomManager sets it up (`__InitThemeSpecificObjects`
// 0xe3ee40, `Update` 0xe43c20; docs/platform/RenderFidelity.md "Render passes"), from the theme
// registry's WS_Environment (world/files/system/wsthemes.thm) and the theme bundle's meshes:
// - the skybox (`m_SkyboxName`) in `DRL_RenderGeometry_GameBG` (the `DRP_TransBG` pass, the
//   background camera), at the origin, clip "Go" playing at speed 1 from the frame the theme
//   objects were created (looping);
// - "FishTankBack" in `FishTankPass:FishTank` and the front fish tank (`m_FishtankName`) in
//   `WaterPassFront:DRL_FishTank`, both at (0, water level, 0) under the game camera;
// - the two water planes (water.ts).
// Not drawn, as in the original: the wind specks mesh (`__InitThemeSpecificObjects` never
// creates `m_pWindSpecksMeshInst`, so `__UpdateWindSpecks` has nothing to pose) and the
// background objects (`BackgroundPass:WS_Background` draws into the main colour, which
// `FishTankPass` clears before anything reads it; every shipped theme also hides them).
// The theme's post-processing values come from Themes.txt (`ThemeMan::LoadResources` 0xc48d20:
// `BloomDefaults`, `VignetteIntensity`).
import * as THREE from 'three';
import { assetJson, assetUrl } from '../assets.ts';
import { findMesh, instantiate, loadTemplate, pose, type MeshInstance } from './meshes.ts';
import { DEFAULT_POST, type PostTheme } from './passes.ts';
import { Water, type WaterEnv } from './water.ts';

type Env = WaterEnv & { m_SkyboxName?: string; m_FishtankName?: string };
type Themes = { list: Record<string, { WSRM_Theme: { m_BundleName: string; m_EnvironmentInfoSerialised: { WS_Environment: Env } } }> };
type DatumNode = { name?: string; value?: string; children?: DatumNode[] };
type XomGraph = { graph: { objects: Record<string, { class: string; png?: string | null }> } };

/** The skybox clip `__InitThemeSpecificObjects` plays (looping, speed 1). */
const SKY_CLIP = 'Go';

/** Draw order inside one instance: back to front by world z, ties in tree order (the converted
 * meshes' render states mostly disable the depth test, AssetConv "glTF conventions"). With
 * `byState` the shapes are first grouped by state class: the skybox layer
 * (`DRL_RenderGeometry_GameBG`, no Sort() in the scene script) uses the cull-sort's default bin 0,
 * which sorts by shader state (`kBinSortState`, `XCullSortAction::XCullSortAction` 0x1241820;
 * `XomCullSortShape` 0x1240410 puts every skybox shape, XShape Flags 0, into that bin). In the
 * original's frames the result is that the opaque BG plane is drawn after the four additive light
 * rays (UI_Solid_Emis1: SrcAlpha/One, no depth write) and paints over them, while the clouds come
 * after it. The exact state key order is not ported; the grouping reproduces that outcome:
 * additive shapes, then depth-writing ones, then the transparent ones. */
function orderByDepth(inst: MeshInstance, byState = false): void {
  inst.root.updateMatrixWorld(true);
  const meshes: { m: THREE.Object3D; z: number; i: number; s: number }[] = [];
  const v = new THREE.Vector3();
  inst.root.traverse(o => {
    if (!(o instanceof THREE.Mesh)) return;
    const mat = o.material as THREE.Material;
    const s = !byState ? 0 : mat.blending === THREE.CustomBlending && mat.blendDst === THREE.OneFactor ? 0 : mat.depthWrite ? 1 : 2;
    meshes.push({ m: o, z: v.setFromMatrixPosition(o.matrixWorld).z, i: meshes.length, s });
  });
  meshes.sort((a, b) => a.s - b.s || a.z - b.z || a.i - b.i);
  meshes.forEach((e, k) => { e.m.renderOrder = k / 1000; });
}

async function meshOf(name: string | undefined, bundle: string, byState = false): Promise<MeshInstance | null> {
  if (!name) return null;
  const f = findMesh(name, bundle, null);
  if (!f) return null;
  const t = await loadTemplate(f.glb, f.wanim);
  if (!t) return null;
  const inst = instantiate(t);
  pose(inst, null, 0);
  orderByDepth(inst, byState);
  return inst;
}

/** The theme's `BloomDefaults` and `VignetteIntensity` (Themes.txt), China's when absent. */
async function postTheme(bundle: string): Promise<PostTheme> {
  const doc = await assetJson<{ root: DatumNode }>('data/scripts/json/CommonData/Scripts/Themes.txt.json');
  const find = (n: DatumNode | undefined, name: string): DatumNode | undefined => n?.children?.find(c => c.name?.toLowerCase() === name.toLowerCase());
  // The theme datums (`ResourceGroups.<Theme>`, `BundleName = "bundles:/<Theme>.bdl"`).
  const t = doc.root.children?.find(c => c.name?.toLowerCase() === bundle.toLowerCase()
    || find(c, 'BundleName')?.value?.toLowerCase().endsWith(`/${bundle.toLowerCase()}.bdl`));
  const num = (n: DatumNode | undefined, fallback: number): number => (n?.value !== undefined ? Number(n.value) : fallback);
  const bloom = find(t, 'BloomDefaults');
  return {
    bloomThreshold: num(find(bloom, 'Threshold'), DEFAULT_POST.bloomThreshold),
    bloomIntensity: num(find(bloom, 'Intensity'), DEFAULT_POST.bloomIntensity),
    bloomBlurSize: num(find(bloom, 'BlurSize'), DEFAULT_POST.bloomBlurSize),
    vignette: num(find(t, 'VignetteIntensity'), DEFAULT_POST.vignette),
  };
}

export class Background {
  /** `DRL_RenderGeometry_GameBG`: the skybox. */
  sky = new THREE.Group();
  /** `FishTankPass:FishTank`. */
  fishTankBack = new THREE.Group();
  /** `WaterPassFront:DRL_FishTank`. */
  fishTankFront = new THREE.Group();
  water = new Water();
  post: PostTheme = DEFAULT_POST;
  /** WorldSystem.bdl `Vignette`. */
  vignette: THREE.Texture | null = null;
  /** The frame (sim tick) the theme objects were created on: the skybox clip's time 0. */
  skyStart = 0;
  private skyInst: MeshInstance | null = null;

  async load(theme: string | null): Promise<void> {
    for (const g of [this.sky, this.fishTankBack, this.fishTankFront]) g.clear();
    this.skyStart = 0;
    this.skyInst = null;
    const vignette = this.vignette ? Promise.resolve(this.vignette) : loadVignette().catch(e => { console.warn('vignette', e); return null; });
    if (!theme) {
      this.vignette = await vignette;
      return;
    }
    const themes = await assetJson<Themes>('world/files/system/wsthemes.thm');
    const rec = Object.values(themes.list).find(t => t.WSRM_Theme.m_BundleName.toLowerCase() === theme.toLowerCase());
    const env = rec?.WSRM_Theme.m_EnvironmentInfoSerialised.WS_Environment ?? null;
    const bundleName = rec?.WSRM_Theme.m_BundleName ?? theme;
    const bundle = `DataLinux/Bundles/${bundleName}.bdl`;
    const [sky, back, front, post, vig] = await Promise.all([
      meshOf(env?.m_SkyboxName, bundle, true), meshOf('FishTankBack', bundle), meshOf(env?.m_FishtankName, bundle),
      postTheme(bundleName).catch(e => { console.warn('theme post', e); return DEFAULT_POST; }), vignette,
      this.water.load(env).catch(e => console.warn('water', e)),
    ]);
    this.post = post;
    this.vignette = vig;
    this.skyInst = sky;
    if (sky) this.sky.add(sky.root);
    if (back) this.fishTankBack.add(back.root);
    if (front) this.fishTankFront.add(front.root);
  }

  /** Per frame: the water level (world y) and the frame counter (sim tick). */
  update(waterLevel: number, frame: number): void {
    const sky = this.skyInst;
    if (sky) {
      // The XSAnim clock advances 1/60 s per presented frame (one sim tick); the clip loops.
      const t = Math.max(0, frame - this.skyStart) / 60;
      const length = sky.template.anim?.clips.find(c => c.name === SKY_CLIP)?.duration ?? 0;
      pose(sky, SKY_CLIP, length > 0 ? t % length : 0);
      sky.root.updateMatrixWorld(true);
    }
    for (const g of [this.fishTankBack, this.fishTankFront]) {
      g.position.set(0, waterLevel, 0);
      g.updateMatrixWorld(true);
    }
  }
}

async function loadVignette(): Promise<THREE.Texture> {
  const desc = await assetJson<XomGraph>('xom/DataLinux/Bundles/WorldSystem/Vignette.json');
  const img = Object.values(desc.graph.objects).find(o => o.class === 'XImage' && o.png);
  if (!img?.png) throw new Error('Vignette: no image');
  const tex = await new THREE.TextureLoader().loadAsync(assetUrl(img.png));
  tex.flipY = false;
  tex.colorSpace = THREE.NoColorSpace;
  tex.generateMipmaps = false;
  tex.minFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}
